/**
 * Nutrition targets and the gentle checks against them (LDHFRT's Goals tab, GearSkeptic's
 * colour key). A miss is an `info` warning: the plan is still a plan.
 *
 * Defaults:
 *   - macro share of energy 15 / 35 / 50 % protein / fat / carb (LDHFRT's defaults), on target
 *     within ±5 points (LDHFRT's tightest tolerance band);
 *   - energy within ±10 % of the goal (LDHFRT);
 *   - sodium 1,000 mg per 1,000 kcal (LDHFRT: 3,500 mg a day at 3,500 kcal). GearSkeptic rates
 *     Na/kcal below 0.64 "below daily allowance" and above 1.25 "heavy": the band is the
 *     target × 0.64 to × 1.25;
 *   - energy density 4.4 kcal/g (125 kcal/oz, the "lightweight" band and the usual thru-hiker
 *     rule of thumb). Only falling short is flagged: denser is never a problem.
 */
import type { MacroTotals, PlanTargets, TargetCheck, TargetReport, Totals, Warning } from './types'
import { perOzToPerG, round } from './units'

export const DEFAULT_TARGETS: PlanTargets = {
  protein_share: 0.15,
  fat_share: 0.35,
  carb_share: 0.5,
  sodium_mg_per_1000kcal: 1000,
  kcal_per_g: round(perOzToPerG(125), 2),
  kcal_tolerance: 0.1,
  macro_tolerance: 0.05,
}

/** Sodium band edges as shares of the target (GearSkeptic's 0.64 and 1.25 mg/kcal). */
export const SODIUM_LOW_SHARE = 0.64
export const SODIUM_HIGH_SHARE = 1.25

const finiteOr = (v: unknown, d: number) => (typeof v === 'number' && Number.isFinite(v) ? v : d)

/** Fill missing or broken fields from the defaults; clamp shares to 0–1. */
export function normaliseTargets(raw: Partial<PlanTargets> | undefined): PlanTargets {
  const r = raw ?? {}
  const share = (v: unknown, d: number) => Math.min(1, Math.max(0, finiteOr(v, d)))
  const pos = (v: unknown, d: number) => Math.max(0, finiteOr(v, d))
  return {
    protein_share: share(r.protein_share, DEFAULT_TARGETS.protein_share),
    fat_share: share(r.fat_share, DEFAULT_TARGETS.fat_share),
    carb_share: share(r.carb_share, DEFAULT_TARGETS.carb_share),
    sodium_mg_per_1000kcal: pos(r.sodium_mg_per_1000kcal, DEFAULT_TARGETS.sodium_mg_per_1000kcal),
    kcal_per_g: pos(r.kcal_per_g, DEFAULT_TARGETS.kcal_per_g),
    kcal_tolerance: share(r.kcal_tolerance, DEFAULT_TARGETS.kcal_tolerance),
    macro_tolerance: share(r.macro_tolerance, DEFAULT_TARGETS.macro_tolerance),
  }
}

function check(actual: number, target: number, low: number, high: number): TargetCheck {
  return {
    actual,
    target,
    low,
    high,
    status: actual < low ? 'low' : actual > high ? 'high' : 'ok',
  }
}

const MACRO_NAMES = { protein: 'Protein', fat: 'Fat', carb: 'Carbs' } as const
const pct = (share: number) => `${Math.round(share * 100)}%`

/**
 * The plan's macros, sodium and density against targets. Nothing is checked without planned
 * energy; sodium "too low" is not claimed when some foods lack macros (the total is a floor).
 */
export function checkTargets(
  planned: Totals,
  macros: MacroTotals,
  targets: PlanTargets,
): { report: TargetReport; warnings: Warning[] } {
  const report: TargetReport = {}
  const warnings: Warning[] = []
  if (planned.kcal <= 0) return { report, warnings }

  const share = macros.energy_share
  if (share) {
    const tol = targets.macro_tolerance
    for (const macro of ['protein', 'fat', 'carb'] as const) {
      const target = targets[`${macro}_share`]
      const c = check(
        round(share[macro], 3),
        target,
        round(target - tol, 3),
        round(target + tol, 3),
      )
      report[macro] = c
      if (c.status !== 'ok') {
        warnings.push({
          level: 'info',
          code: 'macro-off-target',
          macro,
          message: `${MACRO_NAMES[macro]} is ${pct(c.actual)} of energy; target ${pct(target)} ±${Math.round(tol * 100)}`,
        })
      }
    }
  }

  const sodiumTarget = targets.sodium_mg_per_1000kcal
  if (sodiumTarget > 0) {
    const per1000 = round((macros.sodium_mg / planned.kcal) * 1000, 0)
    const c = check(
      per1000,
      sodiumTarget,
      round(sodiumTarget * SODIUM_LOW_SHARE, 0),
      round(sodiumTarget * SODIUM_HIGH_SHARE, 0),
    )
    report.sodium = c
    if (c.status === 'high') {
      warnings.push({
        level: 'info',
        code: 'sodium-high',
        message: `Sodium is ${per1000} mg per 1000 kcal; target ${sodiumTarget}`,
      })
    } else if (c.status === 'low' && !macros.partial) {
      warnings.push({
        level: 'info',
        code: 'sodium-low',
        message: `Sodium is ${per1000} mg per 1000 kcal; target ${sodiumTarget}: pack salt or electrolytes`,
      })
    }
  }

  if (targets.kcal_per_g > 0 && planned.kcal_per_g > 0) {
    const t = targets.kcal_per_g
    // Denser is never a miss: the top of the band is out of reach (pure fat is 9 kcal/g).
    const c = check(planned.kcal_per_g, t, t, Math.max(t, 9))
    report.density = c
    if (c.status === 'low') {
      warnings.push({
        level: 'info',
        code: 'density-low',
        message: `Energy density is ${planned.kcal_per_g} kcal/g; target ${targets.kcal_per_g}`,
      })
    }
  }
  return { report, warnings }
}
